import { createApi } from '@reduxjs/toolkit/query/react';
import { DEFAULT_LANGUAGE } from 'tradera/lang/constants.mjs';
import { touchWebClient } from 'tradera/utils/http';
import { httpClientBaseQuery } from './utils/http-client-base-query';
import qs from 'qs';
import type {
    CategorySearchApiResponse,
    CategorySearchRequest
} from './types/webapi-discover-generated';

import { z } from 'zod';

const CategorySchema = z.object({
    name: z.string(),
    categoryId: z.number(),
    isLeaf: z.boolean()
});

const CategorySuggestionSchema = z.object({
    matchedItemCount: z.number(),
    name: z.string(),
    categoryId: z.number(),
    isLeaf: z.boolean()
});

const CategoriesAndSuggestionsSchema = z.object({
    categories: z.array(CategorySchema),
    // FYI: we don't get these currently in DEV
    suggestions: z.array(CategorySuggestionSchema).optional()
});

type GetCategoriesResponse = z.infer<typeof CategoriesAndSuggestionsSchema>;

type GetCategoriesParams = {
    categoryId: number;
    shortDescription?: string;
    locale?: string;
    currentLevel?: number;
};

export const touchwebSyiApi = createApi({
    reducerPath: 'touchwebSyiApi',
    baseQuery: httpClientBaseQuery({ client: touchWebClient }),
    endpoints: (builder) => ({
        getCategories: builder.query<
            GetCategoriesResponse,
            GetCategoriesParams
        >({
            query: ({
                categoryId = -1,
                shortDescription,
                currentLevel = 0
            }) => {
                if (categoryId > 0 && currentLevel > 0) {
                    return {
                        url: `/selling/categorysuggestions`,
                        method: 'POST',
                        data: {
                            categoryId: categoryId,
                            levelToSearch: currentLevel + 1,
                            title: shortDescription
                        }
                    };
                }

                return {
                    url: '/selling/toplevelcategorysuggestions',
                    method: 'POST',
                    data: {
                        title: shortDescription
                    }
                };
            },

            transformResponse: (response: unknown, _meta, arg) => {
                const data = CategoriesAndSuggestionsSchema.parse(response);

                const suggestions = data?.suggestions || [];

                return {
                    ...data,
                    // just use the first 3 suggestions and merge the rest as regular categories
                    suggestions: suggestions.slice(0, 3),
                    categories: [...data.categories, ...suggestions.slice(3)]
                        ?.slice()
                        .sort((a, b) =>
                            new Intl.Collator(
                                arg.locale ?? DEFAULT_LANGUAGE
                            ).compare(a.name, b.name)
                        )
                };
            }
        }),
        categorySearch: builder.query<
            CategorySearchApiResponse,
            CategorySearchRequest
        >({
            query: (request) =>
                `/api/webapi/discover/category-search?${qs.stringify(request)}`
        })
    })
});

export type Category = z.infer<typeof CategorySchema>;

export const { useCategorySearchQuery, useGetCategoriesQuery, usePrefetch } =
    touchwebSyiApi;
